'use client';
import React, { useMemo } from 'react';
import { DateTime } from 'luxon';
import type { ColumnDef } from '@benzinga/table';
import type { CompanyEventDetails } from '@benzinga/calendar-manager';
import { sanitizeHttpUrl } from '@benzinga/calendars';
import { toInitialCaps } from '@benzinga/utils';
import { formatDate } from './formatters';
import QuoteListTable from './QuoteListTable';

const formatStartTime = (raw?: string): string => {
  if (!raw?.trim()) return '—';
  const trimmed = raw.trim();
  const fromHms = DateTime.fromFormat(trimmed, 'HH:mm:ss');
  if (fromHms.isValid) return fromHms.toFormat('h:mm a');
  const fromIso = DateTime.fromISO(trimmed);
  if (fromIso.isValid) return fromIso.toFormat('h:mm a');
  return trimmed;
};

const DialInCell: React.FC<{ event: CompanyEventDetails }> = ({ event }) => {
  const webcastUrl = sanitizeHttpUrl(event.webcast_link);
  const phone = event.phone_number?.trim();
  if (!webcastUrl && !phone) return <span className="text-gray-400">—</span>;

  return (
    <div className="flex flex-col gap-0.5">
      {webcastUrl ? (
        <a
          className="font-semibold text-blue-600 no-underline hover:text-blue-800"
          href={webcastUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          Join Webcast
        </a>
      ) : null}
      {phone ? (
        <a className="text-blue-600 no-underline hover:text-blue-800" href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
          {phone}
        </a>
      ) : null}
    </div>
  );
};

const companyEventsColumnsDef: ColumnDef<CompanyEventDetails>[] = [
  {
    cellClass: 'text-gray-600 whitespace-nowrap',
    field: 'date_start',
    headerName: 'Date',
    valueFormatter: ({ data }) => formatDate(data.date_start),
  },
  {
    cellClass: 'text-gray-600 whitespace-nowrap',
    field: 'start_time',
    headerName: 'Time',
    valueFormatter: ({ data }) => formatStartTime(data.start_time),
  },
  {
    cellClass: 'text-gray-600 whitespace-nowrap',
    field: 'date_end',
    headerName: 'End Date',
    valueFormatter: ({ data }) =>
      data.date_end && data.date_end !== data.date_start ? formatDate(data.date_end) : '—',
  },
  {
    cellClass: 'font-semibold text-gray-800',
    field: 'event_name',
    headerName: 'Event',
    valueFormatter: ({ data }) => toInitialCaps(data.event_name) || '—',
  },
  {
    cellClass: 'text-gray-600',
    field: 'event_type',
    headerName: 'Type',
    valueFormatter: ({ data }) => toInitialCaps(data.event_type) || '—',
  },
  {
    cellClass: 'text-right text-gray-700',
    field: 'importance',
    headerClass: 'text-right',
    headerName: 'Importance',
    valueFormatter: ({ data }) =>
      data.importance != null && data.importance !== 0 ? String(data.importance) : '—',
  },
  {
    cellClass: 'text-gray-600',
    field: 'location',
    headerName: 'Location',
    valueFormatter: ({ data }) => data.location?.trim() || '—',
  },
  {
    cellRenderer: ({ data }) => <DialInCell event={data} />,
    field: 'webcast_link',
    headerName: 'Dial-in',
  },
  {
    cellRenderer: ({ data }) => {
      const sourceUrl = sanitizeHttpUrl(data.source_link);
      if (!sourceUrl) return <span className="text-gray-400">—</span>;
      return (
        <a
          className="font-semibold text-blue-600 no-underline hover:text-blue-800"
          href={sourceUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          Source
        </a>
      );
    },
    field: 'source_link',
    headerName: 'Links',
  },
];

export const CompanyEventsTable: React.FC<{ events: CompanyEventDetails[] }> = ({ events }) => {
  const sortedEvents = useMemo(
    () =>
      [...events].sort((left, right) => {
        const dateCompare = (right.date_start ?? '').localeCompare(left.date_start ?? '');
        if (dateCompare !== 0) return dateCompare;
        return (right.start_time ?? '').localeCompare(left.start_time ?? '');
      }),
    [events],
  );

  return (
    <QuoteListTable
      columnsDef={companyEventsColumnsDef}
      noDataText="No company events available."
      rowData={sortedEvents}
    />
  );
};

export default CompanyEventsTable;
